import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  canonicalViewUrl,
  decodeViewUrl,
  encodeViewUrl,
  isOwnWrite,
  urlWrite,
  withViewParams,
  type UrlGraph,
  type UrlView,
  type UrlViewState,
  type ViewMode,
} from './viewUrl';

type Options = {
  /** The graph's ids, once it has loaded; nothing is read or written before. */
  graph: UrlGraph | null;
  /** The view as the URL keeps it (see `encodeViewUrl`). */
  view: UrlViewState;
  selectedId: string | null;
  mode: ViewMode;
  /** Sets the page's view to the one a URL names: once on arrival (`arrival`), then on Back, Forward, or a link. */
  onApply: (view: UrlView, arrival: boolean) => void;
};

/**
 * Keeps the view in the URL, both ways. On arrival the URL's view applies while rendering, before anything is
 * drawn. After each change of the view the URL follows: a new focus or selection adds a history entry, so Back
 * returns to the one before; any other change replaces the entry. A URL the page did not write (Back, Forward, a
 * link opened in the same page) applies to the view; the page's own last write is recognised and left alone.
 */
export function useViewUrl({ graph, view, selectedId, mode, onApply }: Options) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [arrived, setArrived] = useState(false);
  if (graph && !arrived) {
    setArrived(true);
    onApply(decodeViewUrl(searchParams, graph), true);
  }
  const encoded = encodeViewUrl(view, selectedId, mode).toString();
  // The URL string last written, or last applied; `null` until the arrival URL is seen.
  const lastWritten = useRef<string | null>(null);
  // The URL last applied (on arrival, or from outside), in the form the view writes it. The view after an apply is
  // exactly the decoded one, so the first write after it is this string, and it replaces: a link or a Back whose
  // dropped ids the page cleans up never gains a history entry. A string, not a flag: an apply that needs no
  // rewrite leaves nothing behind that could turn a later selection into a replace.
  const canonical = useRef<string | null>(null);
  const latest = useRef({ graph, onApply, searchParams, setSearchParams });
  useLayoutEffect(() => {
    latest.current = { graph, onApply, searchParams, setSearchParams };
  });
  // Before paint, so the page never draws a view the URL has left.
  useLayoutEffect(() => {
    if (!arrived) return;
    const url = searchParams.toString();
    const current = latest.current.graph;
    if (isOwnWrite(url, lastWritten.current) || !current) return;
    // The arrival URL was applied while rendering; any other is applied now.
    const arrival = lastWritten.current === null;
    lastWritten.current = url;
    canonical.current = canonicalViewUrl(searchParams, current);
    if (!arrival) latest.current.onApply(decodeViewUrl(searchParams, current), false);
  }, [arrived, searchParams]);
  // Only a change of the view writes: a URL change alone (Back) leaves `encoded` as it was until it applies.
  useEffect(() => {
    if (!arrived) return;
    const url = latest.current.searchParams;
    const next = withViewParams(url, new URLSearchParams(encoded));
    // The page's own last write is the newest URL, even before the router's location catches up with it.
    const known = lastWritten.current === null ? url : new URLSearchParams(lastWritten.current);
    const how = urlWrite(known, next, lastWritten.current, canonical.current, latest.current.graph);
    lastWritten.current = next.toString();
    if (how === null) return;
    canonical.current = null;
    latest.current.setSearchParams(next, { replace: how === 'replace' });
  }, [arrived, encoded]);
}
